import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  BadgeCheck, CalendarClock, Copy, LayoutDashboard, Loader2, LogOut, MessageCircle,
  RefreshCw, Router as RouterIcon, ShieldAlert, Trash2,
} from "lucide-react";
import { api, dt, errMsg, rp, waLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const periodLabel = (months) => (months === 1 ? "/bulan" : months === 12 ? "/tahun" : `/${months} bulan`);

export default function Subscription() {
  const { user, logout, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/subscription/me");
      setData(d);
      setPlan((c) => c || d.pending_request?.plan || d.plan || d.plans?.[0]?.key || "");
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ajukan = async () => {
    if (!plan) return toast.error("Pilih paket dulu");
    setBusy(true);
    try {
      await api.post("/subscription/renew-request", { plan });
      toast.success("Pengajuan dibuat. Lanjutkan pembayaran & konfirmasi via WhatsApp.");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const batalkan = async () => {
    setBusy(true);
    try {
      await api.delete("/subscription/renew-request");
      toast.success("Pengajuan dibatalkan");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const cekAktif = async () => {
    await refresh();
    await load();
    toast.success("Status langganan diperbarui");
  };

  const expired = !!data?.expired;
  const managed = !!data?.managed;
  const pay = data?.payment_info || {};
  const req = data?.pending_request;
  const copy = (t) => { navigator.clipboard?.writeText(String(t || "")); toast.success("Disalin"); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8">
        <header className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-600">
              <RouterIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-display text-lg font-semibold leading-none">{data?.app_name || "ALPHA BILL"}</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Langganan Saya</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!expired && (
              <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="sub-to-dashboard">
                <Link to="/dashboard"><LayoutDashboard className="mr-1.5 h-3.5 w-3.5" /> Ke dasbor</Link>
              </Button>
            )}
            <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={logout} data-testid="sub-logout">
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Keluar
            </Button>
          </div>
        </header>

        {!data ? (
          <p className="text-sm text-muted-foreground">Memuat data langganan…</p>
        ) : (
          <div className="fade-up space-y-6" data-testid="subscription-page">
            <section className={`space-y-3 rounded-2xl border p-6 ${expired ? "border-red-500/40 bg-red-500/5" : "border-border bg-card"}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Akun</p>
                  <p className="font-display text-xl font-semibold">{data.business_name || user?.name || data.username}</p>
                  <p className="font-mono text-xs text-muted-foreground">{data.username}</p>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] ${
                    !managed ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"
                      : expired ? "border-red-500/40 bg-red-500/10 text-red-300"
                        : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  }`}
                  data-testid="sub-status-badge"
                >
                  {!managed ? <><BadgeCheck className="h-3.5 w-3.5" /> Tanpa batas waktu</>
                    : expired ? <><ShieldAlert className="h-3.5 w-3.5" /> Masa langganan berakhir</>
                      : <><BadgeCheck className="h-3.5 w-3.5" /> Aktif</>}
                </span>
              </div>

              {managed && (
                <div className="grid gap-3 text-sm sm:grid-cols-3">
                  <p><span className="text-muted-foreground">Paket:</span> {data.plan_label || data.plan}</p>
                  <p><span className="text-muted-foreground">Berlaku sampai:</span> {data.expires_at ? dt(data.expires_at) : "-"}</p>
                  <p>
                    <span className="text-muted-foreground">Sisa waktu:</span>{" "}
                    <b className={expired ? "text-red-400" : data.days_left <= 7 ? "text-amber-400" : "text-emerald-400"} data-testid="sub-days-left">
                      {expired ? "0 hari" : `${data.days_left} hari`}
                    </b>
                  </p>
                </div>
              )}

              {expired && (
                <p className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="sub-expired-note">
                  Dasbor terkunci karena masa sewa sudah habis. Ajukan perpanjangan di bawah, lakukan pembayaran, lalu konfirmasi via WhatsApp.
                  Akun Anda akan aktif kembali setelah pembayaran dikonfirmasi — semua data pelanggan Anda tetap tersimpan.
                </p>
              )}
              {!expired && managed && data.days_left <= 7 && (
                <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300" data-testid="sub-soon-note">
                  Masa sewa akan berakhir dalam {data.days_left} hari. Perpanjang sekarang agar dasbor tidak terkunci.
                </p>
              )}
              <Button size="sm" variant="outline" className="rounded-full" onClick={cekAktif} data-testid="sub-refresh">
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Muat ulang status
              </Button>
            </section>

            {req ? (
              <section className="space-y-4 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6" data-testid="sub-pending-request">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Pengajuan Perpanjangan</p>
                    <p className="font-mono text-2xl font-semibold text-amber-300" data-testid="sub-req-code">{req.code}</p>
                  </div>
                  <span className="rounded-full border border-amber-500/40 px-3 py-1 text-[11px] text-amber-300">Menunggu konfirmasi</span>
                </div>
                <p className="text-sm">Paket: <b>{req.plan_label}</b> · Nominal: <b>{rp(req.price)}</b></p>
                <div className="space-y-1 rounded-xl bg-secondary/50 p-3 text-xs">
                  {pay.bank_name || pay.account_number ? (
                    <p className="flex items-center justify-between gap-2">
                      <span>{pay.bank_name} · <b className="font-mono text-foreground">{pay.account_number}</b> {pay.account_holder ? `(a.n. ${pay.account_holder})` : ""}</span>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(pay.account_number)}><Copy className="h-3.5 w-3.5" /></Button>
                    </p>
                  ) : null}
                  {pay.ewallet ? <p>E-wallet: <b className="font-mono text-foreground">{pay.ewallet}</b></p> : null}
                  {pay.note ? <p className="text-muted-foreground">{pay.note}</p> : null}
                  {!pay.bank_name && !pay.account_number && !pay.ewallet ? (
                    <p className="text-amber-400">Rekening pembayaran belum diisi admin. Konfirmasi langsung via WhatsApp.</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button asChild className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="sub-wa-confirm">
                    <a href={waLink(data.wa_number, req.wa_message)} target="_blank" rel="noreferrer">
                      <MessageCircle className="mr-1.5 h-4 w-4" /> Konfirmasi via WhatsApp
                    </a>
                  </Button>
                  <Button variant="outline" className="rounded-full" onClick={batalkan} disabled={busy} data-testid="sub-cancel-request">
                    <Trash2 className="mr-1.5 h-4 w-4" /> Batalkan pengajuan
                  </Button>
                </div>
              </section>
            ) : (
              <section className="space-y-4 rounded-2xl border border-border bg-card p-6" data-testid="sub-plan-picker">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-cyan-400" />
                  <p className="text-sm font-medium">Pilih paket perpanjangan</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(data.plans || []).map((p) => {
                    const active = plan === p.key;
                    return (
                      <button
                        type="button" key={p.key} onClick={() => setPlan(p.key)} data-testid={`sub-plan-${p.key}`}
                        className={`rounded-2xl border p-5 text-left transition ${active ? "border-violet-500 bg-violet-500/10 ring-1 ring-violet-500/40" : "border-border hover:border-violet-500/40"}`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{p.label}</p>
                          {active ? <BadgeCheck className="h-4 w-4 text-violet-400" /> : null}
                        </div>
                        <p className="mt-2 font-display text-2xl font-semibold">
                          {rp(p.price)}<span className="text-sm font-normal text-muted-foreground">{periodLabel(Number(p.months))}</span>
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground">{p.desc}</p>
                      </button>
                    );
                  })}
                </div>
                <Button
                  onClick={ajukan} disabled={busy || !plan} data-testid="sub-request-submit"
                  className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white sm:w-auto"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ajukan Perpanjangan"}
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  Setelah diajukan, Anda mendapat kode pembayaran dan tombol konfirmasi WhatsApp. Masa aktif ditambahkan setelah admin mengonfirmasi pembayaran.
                </p>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
